import type { Ref, WritableComputedRef } from 'vue'
import { nextTick, onMounted, ref } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import type { GalleryCarouselCardFragment } from '@/graphql/api'
import type { PromisifyFn } from '@vueuse/shared'

export interface EndlessSlides {
    slidesEndless: Ref<ExtendedSlide[]>
    recalculateSlidesAndRescroll: PromisifyFn<() => void>
    handleTouchStart: () => boolean
    handleTouchEnd: () => boolean
    isUserInteracting: Ref<boolean>
}

export type ExtendedSlide = {
    slide: GalleryCarouselCardFragment
    key: number
}

export function useEndlessSlides({
    slides,
    current,
    container,
    slideWidth,
    containerOffsetWidth,
}: {
    slides: Ref<GalleryCarouselCardFragment[]>
    current: Ref<number> | WritableComputedRef<number>
    container: Ref<HTMLElement | null>
    slideWidth: Ref<number>
    containerOffsetWidth: Ref<number>
}): EndlessSlides {
    // make sure there are enough elements to allow for
    // continuous scrolling before slide recalculation
    const slidesExtended: ExtendedSlide[] = (() => {
        const coeff = Math.round(80 / slides.value.length)

        const s = [] as GalleryCarouselCardFragment[]
        for (let i = 0; i < coeff; i++) {
            s.push(...slides.value)
        }

        const extended = s.map((slide, index) => ({
            slide,
            key: index,
        }))

        return extended
    })()

    // stores the actual arrangement of slides in the carousel
    const slidesEndless: Ref<ExtendedSlide[]> = ref([...slidesExtended])

    // recalculate slides when a user interaction ends
    // basically arranges the element in focus to be
    // in the middle of the array
    const recalculateSlides = () => {
        const half = current.value - Math.floor(slidesEndless.value.length / 2)

        return [
            ...slidesEndless.value.slice(half),
            ...slidesEndless.value.slice(0, half),
        ]
    }

    // scroll to the middle of the array on mount
    onMounted(
        () => (current.value = Math.floor(slidesEndless.value.length / 2))
    )

    // Prevents slide recalculations when user's finger is
    // still touching the screen
    const isUserInteracting = ref(false)
    const handleTouchStart = () => (isUserInteracting.value = true)
    const handleTouchEnd = () => (isUserInteracting.value = false)

    const recalculateSlidesAndRescroll = useDebounceFn(() => {
        // in case the user's finger is still touching the screen
        if (isUserInteracting.value) {
            return
        }

        nextTick(() => {
            slidesEndless.value = recalculateSlides()

            nextTick(() =>
                container.value?.scrollTo({
                    left:
                        Math.floor(slidesExtended.length / 2) *
                            slideWidth.value -
                        containerOffsetWidth.value / 2 +
                        slideWidth.value / 2,
                })
            )
        })
    }, 300)

    return {
        slidesEndless,
        recalculateSlidesAndRescroll,
        handleTouchStart,
        handleTouchEnd,
        isUserInteracting,
    }
}
